import Component from '@glimmer/component';
import { guidFor } from '@ember/object/internals';
import { or } from 'tio-ui/utilities';

// ---------------------------------------------------------------------------
// Checkbox – reimagined single checkbox
// ---------------------------------------------------------------------------

export type CheckboxSize = 'sm' | 'md' | 'lg';

interface CheckboxSignature {
  Args: {
    /**
     * Stable identity for automation: emitted as `data-test-hook` on this
     * component's root, alongside its `data-test-*` kind markers.
     *
     * The call site says WHICH instance this is; the component already says
     * what KIND it is. Together they give a key that survives a re-render,
     * which `id` does not — 74% of ids in these apps are Ember-generated
     * (`…-ember42`) and change whenever the component tree is rebuilt. One
     * hook covers the whole subtree: a descendant control is identified as
     * its nearest ancestor hook plus its own role.
     *
     * Consumed by the ux-audit's per-element interaction ledger to tell "a
     * change broke this control" from "the page re-rendered in a different
     * order". See tuitionio/tio-front-end#10057.
     *
     * @example <Checkbox @testHook="tas-approval-queue__select-all" @value={{this.all}} />
     */
    testHook?: string;
    /** Whether the checkbox is checked */
    checked?: boolean;
    /** Called when the checked state changes */
    onChange?: (checked: boolean) => void;
    /** Label text displayed next to the checkbox */
    label?: string;
    /** Hint text displayed below the label */
    hint?: string;
    /** Validation error messages */
    errors?: string[];
    /** Disable the checkbox */
    isDisabled?: boolean;
    /** Show indeterminate state (overrides checked visually) */
    isIndeterminate?: boolean;
    /** Size variant @default "md" */
    size?: CheckboxSize;
  };
  Element: HTMLDivElement;
}

// ---------------------------------------------------------------------------
// Size classes
// ---------------------------------------------------------------------------

const SIZE_CLASSES: Record<CheckboxSize, { box: string; label: string; icon: string }> = {
  sm: { box: 'w-3.5 h-3.5', label: 'text-sm', icon: 'w-2.5 h-2.5' },
  md: { box: 'w-4 h-4', label: 'text-sm', icon: 'w-3 h-3' },
  lg: { box: 'w-5 h-5', label: 'text-base', icon: 'w-3.5 h-3.5' },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Reimagined Checkbox — a zero-dependency checkbox component.
 *
 * Controlled checkbox that fires `@onChange` with the new boolean value.
 * Supports indeterminate state, labels, hints, and error display.
 *
 * @example Basic usage
 * ```gts
 * import Checkbox from 'tio-ui/components/checkbox';
 *
 * <Checkbox
 *   @label="I agree to the terms"
 *   @checked={{this.agreed}}
 *   @onChange={{this.setAgreed}}
 * />
 * ```
 *
 * @example With errors
 * ```gts
 * <Checkbox
 *   @label="Accept privacy policy"
 *   @checked={{this.accepted}}
 *   @onChange={{this.setAccepted}}
 *   @errors={{array "You must accept the privacy policy"}}
 * />
 * ```
 */
export default class Checkbox extends Component<CheckboxSignature> {
  private _fieldId = `reimagined-checkbox-${guidFor(this)}`;

  private get size(): CheckboxSize {
    return this.args.size ?? 'md';
  }

  private get sizeClasses() {
    return SIZE_CLASSES[this.size];
  }

  private get hasErrors(): boolean {
    return Boolean(this.args.errors?.length);
  }

  handleChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    this.args.onChange?.(input.checked);
  };

  <template>
    <div
      data-test-checkbox
      data-test-hook={{@testHook}}
      class="tio-col-extra-tight {{if @isDisabled 'opacity-50'}}"
      ...attributes
    >
      <label
        data-test-checkbox-label
        for={{this._fieldId}}
        class="flex items-start gap-2.5 cursor-pointer select-none
          {{if @isDisabled 'cursor-not-allowed'}}"
      >
        {{! Custom checkbox visual }}
        <span class="relative shrink-0 mt-0.5">
          <input
            id={{this._fieldId}}
            type="checkbox"
            checked={{@checked}}
            disabled={{@isDisabled}}
            aria-checked={{if @isIndeterminate "mixed" (if @checked "true" "false")}}
            aria-invalid={{if this.hasErrors "true"}}
            class="peer sr-only"
            {{on "change" this.handleChange}}
          />
          <span
            class="tio-checkbox
              {{this.sizeClasses.box}}
              {{if (or @isIndeterminate @checked) 'tio-checkbox--checked'}}
              {{if this.hasErrors 'tio-checkbox--error'}}"
          >
            {{#if @isIndeterminate}}
              {{! Indeterminate mark (takes priority over checked) }}
              <svg
                class="{{this.sizeClasses.icon}} tio-fg-inverse"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.5 6H9.5"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            {{else if @checked}}
              {{! Check mark SVG }}
              <svg
                class="{{this.sizeClasses.icon}} tio-fg-inverse"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.5 6L5 8.5L9.5 3.5"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            {{/if}}
          </span>
        </span>

        {{! Label + hint text }}
        <div class="tio-col-flush">
          {{#if @label}}
            <span class="{{this.sizeClasses.label}} font-medium tio-fg-secondary">
              {{@label}}
            </span>
          {{/if}}
          {{#if @hint}}
            <span class="text-xs tio-fg-muted mt-0.5">{{@hint}}</span>
          {{/if}}
        </div>
      </label>

      {{! Error messages }}
      {{#if this.hasErrors}}
        <div data-test-checkbox-errors class="ml-6 space-y-0.5">
          {{#each @errors as |error|}}
            <p class="text-xs tio-fg-danger" role="alert">{{error}}</p>
          {{/each}}
        </div>
      {{/if}}
    </div>
  </template>
}

export type { CheckboxSignature };
